import { createContext, type ReactNode, useContext, useEffect } from "react";
import { type DraftOwner, keepOwnedWorkingDrafts, workingDraftKey } from "../lib/working-draft";

const Owner = createContext<string | null>(null);
const Principal = createContext<DraftOwner | null>(null);
const Interview = createContext(false);
const Messaging = createContext(false);
const Service = createContext(false);
const Performance = createContext(false);

/** The verified caller, including when requisition drafts are not licensed. */
export function useDraftOwner(): DraftOwner | null {
  return useContext(Principal);
}

/** A verified owner only while the original messaging module remains licensed. */
export function useMessagingDraftOwner(): DraftOwner | null {
  const owner = useContext(Principal);
  return useContext(Messaging) ? owner : null;
}

/** Identity comes from the verified session; no extra session read per form. */
export function WorkingDraftProvider({
  owner,
  enabled,
  interviewEnabled = false,
  messagingEnabled = false,
  serviceEnabled = false,
  performanceEnabled = false,
  children,
}: {
  readonly owner: DraftOwner;
  readonly enabled: boolean;
  readonly interviewEnabled?: boolean;
  readonly messagingEnabled?: boolean;
  readonly serviceEnabled?: boolean;
  readonly performanceEnabled?: boolean;
  readonly children: ReactNode;
}) {
  const key = enabled ? workingDraftKey(owner, "requisition") : null;
  useEffect(
    () =>
      keepOwnedWorkingDrafts(
        owner,
        enabled,
        interviewEnabled,
        messagingEnabled,
        serviceEnabled,
        performanceEnabled,
      ),
    [owner, enabled, interviewEnabled, messagingEnabled, serviceEnabled, performanceEnabled],
  );
  return (
    <Principal.Provider
      key={JSON.stringify([
        owner.issuer,
        owner.tenant_id,
        owner.subject,
        owner.person?.person_id ?? null,
        enabled,
        interviewEnabled,
        messagingEnabled,
        serviceEnabled,
        performanceEnabled,
      ])}
      value={owner}
    >
      <Interview.Provider value={interviewEnabled}>
        <Owner.Provider key={key} value={key}>
          <Messaging.Provider value={messagingEnabled}>
            <Service.Provider value={serviceEnabled}>
              <Performance.Provider value={performanceEnabled}>{children}</Performance.Provider>
            </Service.Provider>
          </Messaging.Provider>
        </Owner.Provider>
      </Interview.Provider>
    </Principal.Provider>
  );
}

/** Shared context identities; form storage/timers stay in the deferred hook module. */
export const DraftContexts = {
  requisition: Owner,
  principal: Principal,
  interview: Interview,
  service: Service,
  performance: Performance,
};
